/**
 * Lineage and Issue Tracking API Service
 * 
 * Provides methods to interact with the lineage discovery and issue tracking APIs.
 * This service connects the onboarding dashboard to the new lineage features.
 */

import axios from 'axios';
import { getSession } from './supabase';
import { buildDedupKey, dedupedRequest } from '../utils/requestDedup';

// Use the same API base URL as the main API service
const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:5000/api'  // Local development
  : process.env.REACT_APP_API_BASE_URL || 'https://sparvi-webapp-fjdjdvh2bse9d0gm.centralus-01.azurewebsites.net/api';

// Create axios instance with the same configuration as enhancedApiService
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication (same as enhancedApiService)
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const session = await getSession();
      const token = session?.access_token;
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (err) {
      // Silent auth error
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(error)
);

// Wrap GET/HEAD with cross-library in-flight dedup. Without this,
// DataQualityCommandCenter's raw useEffect → getLineageStatus() fires
// once per mount (StrictMode = 2x) and competes with other dashboard
// requests for the browser's 6-connection HTTP/1.1 pool. Mutations are
// not wrapped because each caller expects their own write to land.
const originalGet = apiClient.get.bind(apiClient);
apiClient.get = function dedupedGet(url, config = {}) {
  const dedupKey = buildDedupKey('GET', url, config.params);
  return dedupedRequest(dedupKey, () => originalGet(url, config));
};
const originalHead = apiClient.head.bind(apiClient);
apiClient.head = function dedupedHead(url, config = {}) {
  const dedupKey = buildDedupKey('HEAD', url, config.params);
  return dedupedRequest(dedupKey, () => originalHead(url, config));
};

class LineageApiService {
  constructor() {
    this.client = apiClient;
  }

  /**
   * Handle API responses and errors
   */
  async handleResponse(response) {
    return response.data;
  }

  // =============================================================================
  // LINEAGE DISCOVERY METHODS
  // =============================================================================

  /**
   * Trigger lineage discovery for a connection
   * @param {string} connectionId - Database connection ID
   * @returns {Promise<Object>} Discovery result
   */
  async discoverLineage(connectionId) {
    const response = await this.client.post('/lineage/discovery/start', {
      connection_id: connectionId
    });
    return await this.handleResponse(response);
  }

  /**
   * Resolve a full_name (e.g., DATABASE.SCHEMA.TABLE) to its UUID.
   */
  async resolveObjectByName(fullName) {
    try {
      const response = await this.client.get('/lineage/resolve', {
        params: { full_name: fullName }
      });
      return await this.handleResponse(response);
    } catch (error) {
      return { success: false, data: null, error: error.message };
    }
  }

  /**
   * Get lineage information for a specific data object
   */
  async getObjectLineage(objectId) {
    const encodedId = encodeURIComponent(objectId);
    const response = await this.client.get(`/lineage/object/${encodedId}`);
    return await this.handleResponse(response);
  }

  /**
   * Get all data objects with optional filtering
   */
  async getDataObjects(filters = {}) {
    const params = {};
    if (filters.object_type) params.object_type = filters.object_type;
    if (filters.business_criticality) params.business_criticality = filters.business_criticality;
    if (filters.database) params.database = filters.database;
    if (filters.schema) params.schema = filters.schema;
    if (filters.search) params.search = filters.search;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;

    const response = await this.client.get('/lineage/objects', { params });
    return await this.handleResponse(response);
  }

  /**
   * Get lineage discovery status
   */
  async getLineageStatus() {
    const response = await this.client.get('/lineage/status');
    return await this.handleResponse(response);
  }

  /**
   * Get discovery run history for a connection
   */
  async getDiscoveryRuns(connectionId) {
    const response = await this.client.get('/lineage/discovery/runs');
    return await this.handleResponse(response);
  }

  // =============================================================================
  // ISSUE TRACKING METHODS
  // =============================================================================

  async getIssuesForDashboard(filters = {}) {
    const response = await this.client.get('/issues/dashboard-data');
    return await this.handleResponse(response);
  }

  async getIssues(filters = {}) {
    const params = {};
    if (filters.status && filters.status !== 'all') params.status = filters.status;
    if (filters.severity && filters.severity !== 'all') params.severity = filters.severity;
    if (filters.assignedTo) params.assigned_to = filters.assignedTo;
    if (filters.limit) params.limit = filters.limit;

    const response = await this.client.get('/issues', { params });
    return await this.handleResponse(response);
  }

  async getIssue(issueId) {
    const response = await this.client.get(`/issues/${issueId}`);
    return await this.handleResponse(response);
  }

  async createIssue(issueData) {
    const response = await this.client.post('/issues', issueData);
    return await this.handleResponse(response);
  }

  async createIssueFromValidation(validationResult, connectionId) {
    const response = await this.client.post('/issues/from-validation', {
      validation_result: validationResult,
      connection_id: connectionId
    });
    return await this.handleResponse(response);
  }

  async resolveIssue(issueId, resolutionData) {
    const response = await this.client.post(`/issues/${issueId}/resolve`, resolutionData);
    return await this.handleResponse(response);
  }

  async assignIssue(issueId, assignmentData) {
    const response = await this.client.post(`/issues/${issueId}/assign`, assignmentData);
    return await this.handleResponse(response);
  }

  async getIssueStats() {
    const response = await this.client.get('/issues/stats');
    return await this.handleResponse(response);
  }

  // =============================================================================
  // STAKEHOLDER MANAGEMENT METHODS
  // =============================================================================

  async getStakeholderGroups() {
    const response = await this.client.get('/lineage/stakeholders');
    return await this.handleResponse(response);
  }

  async createStakeholderGroup(groupData) {
    const response = await this.client.post('/lineage/stakeholders', groupData);
    return await this.handleResponse(response);
  }

  async getObjectStakeholders(objectId) {
    const response = await this.client.get(`/lineage/objects/${objectId}/stakeholders`);
    return await this.handleResponse(response);
  }

  async analyzeImpact(impactData) {
    const response = await this.client.post('/lineage/impact-analysis', impactData);
    return await this.handleResponse(response);
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  async isLineageEnabled() {
    try {
      const status = await this.getLineageStatus();
      return status.success && status.data.lineage_enabled;
    } catch (error) {
      return false;
    }
  }

  async getDashboardSummary() {
    try {
      const [lineageStatus, issueStats, issues] = await Promise.all([
        this.getLineageStatus().catch(() => ({ success: false, data: { lineage_enabled: false } })),
        this.getIssueStats().catch(() => ({ success: false, data: {} })),
        this.getIssuesForDashboard().catch(() => ({ success: false, data: { issues: {}, summary: {} } }))
      ]);

      return {
        success: true,
        data: {
          lineage: lineageStatus.success ? lineageStatus.data : { lineage_enabled: false },
          issueStats: issueStats.success ? issueStats.data : {},
          issues: issues.success ? issues.data : { issues: {}, summary: {} }
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.message,
        data: {
          lineage: { lineage_enabled: false },
          issueStats: {},
          issues: { issues: {}, summary: {} }
        }
      };
    }
  }
}

// Export singleton instance
const lineageApiService = new LineageApiService();
export default lineageApiService;